import { useEffect, useState } from "react";

import { Link } from "react-router-dom";

import EngagementGapQuadrant from "./EngagementGapQuadrant";
import GapBadge from "./GapBadge";
import MiniSparkline from "./MiniSparkline";
import PortfolioTrendChart from "./PortfolioTrendChart";
import {
  HubPageLoading,
  hubModalLoadingClassName,
} from "../../Components/EngagementEmptyPanel";
import CompositeDonut from "../../Pages/MyCheckIns/CompositeDonut";
import { useAppDispatch } from "../../Store/Hooks";
import { fetchSponsorDrilldown } from "../../Store/Reducers/SponsorEngagementSlice";
import {
  S7_ACCOUNT_CARD,
  S7_ACCOUNT_CARD_H,
  S7_ACCOUNT_META,
  S7_ACCT_SUB,
  S7_DT,
  S7_DT_TD,
  S7_DT_TD_NUM,
  S7_DT_TH,
  S7_DT_TH_NUM,
  S7_DT_TR,
  S7_GRID,
  S7_ROOT,
  sponsorDrilldownStateBarClass,
} from "../../Utils/Helper/discoveryHubUi";
import {
  fmtScore,
  leaderboardStateTitle,
  MCI_MUTED,
  sponsorInitials,
  tierDotClass,
  tierPillClass,
} from "../../Utils/Helper/sponsorEngagementUi";
import { avatarColor } from "../AssessmentsContainer/DisconnectTriggerModal";

type Props = {
  sponsorId: string;
  period?: string | null;
  cycleId?: string | null;
  backTo: string;
  /** Fill a fixed-height modal body while loading (Poll History drilldown). */
  fillShell?: boolean;
};

const TierPill = ({ tier }: { tier: string | null | undefined }) => {
  if (!tier) return <span className={MCI_MUTED}>—</span>;
  return (
    <span className={tierPillClass(tier)}>
      <span className={tierDotClass(tier)} />
      {tier}
    </span>
  );
};

const SponsorDrilldownPanel = ({
  sponsorId,
  period,
  cycleId,
  backTo,
  fillShell = false,
}: Props) => {
  const dispatch = useAppDispatch();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params: Record<string, string> = {};
    if (period) params.period = period;
    if (cycleId) params.cycle_id = cycleId;
    dispatch(
      fetchSponsorDrilldown({
        sponsorId,
        params: Object.keys(params).length ? params : undefined,
      })
    )
      .then(result => {
        if (fetchSponsorDrilldown.fulfilled.match(result)) {
          setData(result.payload);
        } else {
          setData(null);
        }
      })
      .finally(() => setLoading(false));
  }, [dispatch, sponsorId, period, cycleId]);

  if (loading) {
    return (
      <div
        className={`${S7_ROOT}${fillShell ? " flex min-h-0 flex-1 flex-col" : ""}`}
      >
        <HubPageLoading
          message="Loading sponsor data…"
          className={fillShell ? hubModalLoadingClassName : undefined}
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className={`${S7_ROOT} p-8 text-center text-gray-500 ${
          fillShell
            ? "min-h-0 flex-1 flex flex-col items-center justify-center"
            : ""
        }`}
      >
        <p className="text-sm">Unable to load sponsor drilldown.</p>
        <Link
          to={backTo}
          className="text-purple-700 font-semibold text-sm mt-2 inline-block"
        >
          ← Back
        </Link>
      </div>
    );
  }

  const header = data.header ?? {};
  const accounts: any[] = data.accounts ?? [];
  const pubState = data.publication_state ?? "draft";
  const snapshotPeriod = data.period ?? "—";
  const rank = header.rank;
  const totalSponsors = data.total_sponsors ?? "—";
  const composite = header.composite;
  const tier = header.tier;
  const gl = data.gl ?? header.gl ?? "—";
  const theater = data.theater ?? header.theater ?? "—";
  const region = data.region ?? header.region ?? "—";
  const metaParts = [gl, theater, theater !== region ? region : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className={S7_ROOT}>
      {/* ── Status banner ── */}
      <div className={`${sponsorDrilldownStateBarClass(pubState)} mb-4`}>
        <span>{leaderboardStateTitle(pubState, snapshotPeriod)}</span>
        <span className="ml-auto text-[11px] font-medium opacity-70">
          {snapshotPeriod} · live data
        </span>
      </div>

      {/* ── Hero card ── */}
      <div className="rounded-xl overflow-hidden mb-4 bg-white border border-gray-100 shadow-sm">
        <div
          className={`h-[3px] ${
            tier === "Champion"
              ? "bg-green-500"
              : tier === "Active"
                ? "bg-blue-500"
                : tier === "Developing"
                  ? "bg-amber-500"
                  : tier === "At-Risk"
                    ? "bg-red-500"
                    : "bg-purple-500"
          }`}
        />
        <div className="flex items-center gap-4 px-5 py-3.5 bg-gradient-to-r from-gray-50 to-white">
          {/* Avatar — per-person hash color, never hardcoded */}
          <div
            className="w-12 h-12 rounded-full text-white flex items-center justify-center text-[15px] font-bold shrink-0 ring-2 ring-white shadow"
            style={{ backgroundColor: avatarColor(data.sponsor_name) }}
          >
            {sponsorInitials(data.sponsor_name)}
          </div>

          {/* Identity: name + chips on one line, meta below */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[17px] font-bold text-gray-900 leading-none shrink-0">
                {data.sponsor_name ?? "Sponsor"}
              </span>
              {rank != null && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-purple-50 text-purple-700 border border-purple-200 rounded-md px-1.5 py-[3px] leading-none shrink-0">
                  <span className="text-[9px] uppercase tracking-widest font-bold text-purple-400">
                    Rank
                  </span>
                  <span className="text-purple-200">|</span>
                  <span className="font-bold text-purple-800">#{rank}</span>
                  <span className="text-purple-300">of</span>
                  <span className="text-purple-600">{totalSponsors}</span>
                </span>
              )}
              {accounts.length > 0 && (
                <span className="text-[10px] font-semibold bg-gray-100 text-gray-600 border border-gray-200 rounded-md px-1.5 py-[3px] leading-none shrink-0 uppercase tracking-wide">
                  {accounts.length} Acct{accounts.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            <div className="text-[13px] text-gray-400 mt-1 leading-snug">
              {[metaParts, snapshotPeriod].filter(Boolean).join(" · ")}
            </div>
            {tier && (
              <div className="mt-1.5">
                <TierPill tier={tier} />
              </div>
            )}
          </div>

          {/* Composite donut — preserved as-is */}
          <div className="shrink-0">
            <CompositeDonut
              composite={composite}
              periodLabel="Composite"
              tier={tier}
              size={100}
            />
          </div>
        </div>
      </div>

      {/* ── Charts grid ── */}
      <div className={`${S7_GRID} mb-4`}>
        <EngagementGapQuadrant
          points={data.quadrant ?? []}
          period={snapshotPeriod}
          gapSplit={data.gap_quadrant_threshold}
        />
        <PortfolioTrendChart points={data.trend ?? []} />
      </div>

      {/* ── Per-Account Detail table ── */}
      <div className={S7_ACCOUNT_CARD}>
        <div className={S7_ACCOUNT_CARD_H}>
          Per-Account Detail
          <span className={S7_ACCOUNT_META}>
            {accounts.length} {accounts.length === 1 ? "account" : "accounts"} ·{" "}
            {snapshotPeriod}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className={`${S7_DT} min-w-[640px]`}>
            <colgroup>
              <col style={{ width: "28%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "12%" }} />
            </colgroup>
            <thead>
              <tr>
                <th className={S7_DT_TH}>Account</th>
                <th className={S7_DT_TH_NUM}>Poll 1</th>
                <th className={S7_DT_TH_NUM}>Poll 2</th>
                <th className={S7_DT_TH_NUM}>Composite</th>
                <th className={S7_DT_TH}>Tier</th>
                <th className={S7_DT_TH}>Gap</th>
                <th className={S7_DT_TH}>Trend (5Q)</th>
              </tr>
            </thead>
            <tbody>
              {accounts.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="py-10 text-center text-sm text-gray-400"
                  >
                    No accounts assigned for this cycle.
                  </td>
                </tr>
              ) : (
                accounts.map((acct: any) => (
                  <tr key={acct.account_id ?? acct.id} className={S7_DT_TR}>
                    <td className={S7_DT_TD}>
                      <strong className="block truncate text-gray-800">
                        {acct.account_name}
                      </strong>
                      <div className={`${S7_ACCT_SUB} truncate`}>
                        {acct.theater ?? theater}
                      </div>
                    </td>
                    <td className={S7_DT_TD_NUM}>{fmtScore(acct.p1_5pt, 1)}</td>
                    <td className={S7_DT_TD_NUM}>{fmtScore(acct.p2_5pt, 1)}</td>
                    <td className={S7_DT_TD_NUM}>
                      <strong className="text-gray-900">
                        {fmtScore(acct.composite, 2)}
                      </strong>
                    </td>
                    <td className={S7_DT_TD}>
                      <TierPill tier={acct.tier} />
                    </td>
                    <td className={S7_DT_TD}>
                      <GapBadge gap={acct.gap} />
                    </td>
                    <td className={S7_DT_TD}>
                      <MiniSparkline series={acct.trend_5q ?? []} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SponsorDrilldownPanel;
